import { useContext, useMemo } from "react";
import {
  BlankMediaTypeImporterExpropriationViewModel,
  SubmitBlankMediaImporterApplication,
} from "../../../../../../generated/data-contracts";
import { AvisContext } from "../../../../../shared/context";
import {
  IAvisEditableListFieldConfig,
  IRowState,
} from "../../../../../shared/components/editableList";
import {
  AvisInputDecimal,
  AvisInputInt,
} from "../../../../../shared/components/inputs";
import { fieldCollectionEquipmentAndCNCode } from "./fields/SharedFieldCollection";
import { ApplicationSectionBase, ISectionSharedProps } from "../../forms";

export function BlankMediaApplicationExpropriations(
  props: Readonly<ISectionSharedProps<SubmitBlankMediaImporterApplication>>,
) {
  const { bag, bagField, equipments, disabled, loading } = props;
  const { res } = useContext(AvisContext);
  const sharedFields = useMemo(
    () => fieldCollectionEquipmentAndCNCode(equipments, res, disabled),
    [equipments, res, disabled],
  );

  const isCalculateMode = (
    count: number | undefined,
    price: number | undefined,
  ) => {
    return !!count && !!price;
  };
  const isManualMode = (
    count: number | undefined,
    price: number | undefined,
    amount: number | undefined,
  ) => {
    return (!count || !price) && !!amount;
  };
  const fieldCollection: IAvisEditableListFieldConfig<
    IRowState<BlankMediaTypeImporterExpropriationViewModel>
  >[] = [
    ...sharedFields,
    {
      name: "count",
      title: res("blankMedia_Application_label_Count"),
      description: res("blankMedia_Application_label_CountDescription"),
      minWidth: 75,
      width: 75,
      renderField: (field, prefix, i, val, onChange) => {
        const disabledAdditional = isManualMode(
          val.count,
          val.price,
          val.amount,
        );
        return (
          <AvisInputInt
            name={`${prefix}.${i}.${field.name}`}
            value={disabledAdditional ? "" : val[field.name]?.toString()}
            onChange={(e) => {
              const valueNew: number | undefined = !e.value
                ? undefined
                : Number(e.value);
              const calculateAmountOld = isCalculateMode(val.count, val.price);
              const calculateAmount = isCalculateMode(valueNew, val.price);
              onChange(e);
              if (calculateAmount || calculateAmountOld) {
                const newAmount = !valueNew
                  ? undefined
                  : +((valueNew || 0) * (val.price ?? 0)).toFixed(2);
                bag.setFieldValue(`${prefix}.${i}.amount`, newAmount);
              }
            }}
            min={1}
            disabled={disabled || disabledAdditional}
            hideClear
          />
        );
      },
    },
    {
      name: "price",
      title: res("blankMedia_Application_label_Price"),
      description: res("blankMedia_Application_label_PriceDescription"),
      dataType: "Double",
      width: 100,
      renderField: (field, prefix, i, val, onChange) => {
        const disabledAdditional = isManualMode(
          val.count,
          val.price,
          val.amount,
        );
        return (
          <AvisInputDecimal
            name={`${prefix}.${i}.${field.name}`}
            value={disabledAdditional ? "" : val[field.name]?.toString()}
            onChange={(e) => {
              const valueNew: number | undefined = !e.value
                ? undefined
                : Number(e.value);
              const calculateAmountOld = isCalculateMode(val.count, val.price);
              const calculateAmount = isCalculateMode(val.count, valueNew);
              onChange(e);
              if (calculateAmount || calculateAmountOld) {
                const newAmount = !valueNew
                  ? undefined
                  : +((val.count ?? 0) * (valueNew || 0)).toFixed(2);
                bag.setFieldValue(`${prefix}.${i}.amount`, newAmount);
              }
            }}
            disabled={disabled || disabledAdditional}
            hideClear
          />
        );
      },
    },
    {
      name: "amount",
      title: res("blankMedia_Application_label_Amount"),
      description: res("blankMedia_Application_label_AmountDescription"),
      width: 100,
      dataType: "Double",
      renderField: (field, prefix, i, val, onChange) => {
        const calculateMode = isCalculateMode(val.count, val.price);
        return (
          <AvisInputDecimal
            name={`${prefix}.${i}.${field.name}`}
            value={
              calculateMode
                ? +((val.count ?? 0) * (val.price ?? 0)).toFixed(2)
                : val[field.name]?.toString()
            }
            onChange={(e) => {
              const valueNew: number | undefined = !e.value
                ? undefined
                : Number(e.value);
              const mustClearValues = isManualMode(
                val.count,
                val.price,
                valueNew,
              );

              onChange(e);
              if (mustClearValues) {
                //if manual amount input - ensure to clear count & price
                bag.setFieldValue(`${prefix}.${i}.count`, undefined);
                bag.setFieldValue(`${prefix}.${i}.price`, undefined);
              }
            }}
            min={1}
            disabled={disabled || calculateMode}
            hideClear
          />
        );
      },
    },
  ];

  return (
    <ApplicationSectionBase
      title={res("blankMedia_Application_section_I_Expropriation")}
      textToDisplay="portal_TDN_ImporterReport_Expropriation"
      bag={bag}
      bagField={bagField}
      initValue={
        {
          rowState: "Add",
        } as IRowState<BlankMediaTypeImporterExpropriationViewModel>
      }
      fieldCollection={fieldCollection}
      loading={loading}
      disabled={disabled}
      forceUpdateKey={{ loading }}
    />
  );
}
